/**
 * Lists on tasks (v0.1.227). "A task can have a list, or a list can create tasks." The task is what you clock into;
 * the list is what's in it — a store run's groceries, a workout's sets. Items are information, not work: no timer,
 * no card, no completion stamp, never counted as tasks. Ticked or not, that's all.
 *
 *   task.list = { templateId?, name?, items: [{ id, text, done, qty? }] }      (on the task doc — docs/SYNC.md §1)
 *   listTemplates/{id} = { name, icon?, items: [{ text, qty? }], departmentId?, uses, lastUsedAt }
 *
 * qty is optional and free: a number (2) or a short amount ("2 L", "1 dozen"). Pure — no I/O.
 */

export const LIST_MAX_ITEMS = 200;
export const LIST_ITEM_MAX = 200;      // characters of one item's text
const QTY_MAX = 20;

const str = (v) => String(v ?? '').trim();

/** A clean qty: a positive number, a short string ("2 L"), or null. */
export function cleanQty(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
  const s = str(v).slice(0, QTY_MAX);
  if (!s) return null;
  return /^\d+(\.\d+)?$/.test(s) ? (Number(s) > 0 ? Number(s) : null) : s;
}

/** One item, clean. A bare string is its text. `newId` gives it an id when it has none. */
export function cleanItem(o, newId) {
  const it = typeof o === 'string' ? { text: o } : (o || {});
  const text = str(it.text).replace(/\s+/g, ' ').slice(0, LIST_ITEM_MAX);
  if (!text) return null;
  const id = it.id ? String(it.id) : newId ? newId() : null;
  if (!id) throw new Error('a list item needs an id (pass newId)');
  const qty = cleanQty(it.qty);
  return { id, text, done: it.done === true, ...(qty != null ? { qty } : {}) };
}

/** A clean list (unknown fields dropped). Items past LIST_MAX_ITEMS are dropped. */
export function makeList(o = {}, newId) {
  const items = (Array.isArray(o.items) ? o.items : []).map((x) => cleanItem(x, newId)).filter(Boolean).slice(0, LIST_MAX_ITEMS);
  return {
    ...(str(o.templateId) ? { templateId: str(o.templateId) } : {}),
    ...(str(o.name) ? { name: str(o.name).slice(0, 80) } : {}),
    items,
  };
}

/** The task's list, or null when it has none (null / missing / no items and no name). */
export function taskList(task) {
  const l = task?.list;
  if (!l || !Array.isArray(l.items)) return null;
  return l;
}

/** { done, total, left } — "4 of 9". */
export function listProgress(list) {
  const items = Array.isArray(list?.items) ? list.items : [];
  const done = items.filter((x) => x.done).length;
  return { done, total: items.length, left: items.length - done };
}

/** The next `n` unticked items, in list order (the running card shows 3). */
export function nextUnticked(list, n = 3) {
  return (Array.isArray(list?.items) ? list.items : []).filter((x) => !x.done).slice(0, n);
}

/** "2 L milk" style label: qty first when it's a number ("2 × eggs"), else after ("milk · 2 L"). */
export function itemLabel(it) {
  if (!it) return '';
  if (it.qty == null) return it.text;
  return typeof it.qty === 'number' ? `${it.qty} × ${it.text}` : `${it.text} · ${it.qty}`;
}

/**
 * Typed text → items: one per line, or comma-separated on one line. A leading "- " / "* " / "[ ] " is dropped; "[x] "
 * is a ticked item. A quantity written "eggs x12" / "eggs ×12" or "12x eggs" / "12 × eggs" becomes qty
 * ("2x4 studs" stays text — no space after the x). "oil · 1 L" = a free-text qty (what listItemsText writes).
 */
export function parseListText(text) {
  const raw = String(text ?? '');
  const parts = raw.includes('\n') ? raw.split('\n') : raw.split(',');
  const out = [];
  for (let p of parts) {
    p = p.trim();
    if (!p) continue;
    let done = false;
    const box = p.match(/^(?:[-*•]\s+)?\[([ xX])\]\s*/);
    if (box) { done = box[1] !== ' '; p = p.slice(box[0].length); } else p = p.replace(/^[-*•]\s+/, '');
    let qty = null;
    const pre = p.match(/^(\d+(?:\.\d+)?)\s*[x×]\s+(.+)$/i);
    const post = p.match(/^(.+?)\s+[x×]\s*(\d+(?:\.\d+)?)$/i);
    const dot = p.match(/^(.+?)\s+·\s+(.+)$/);
    if (pre) { qty = Number(pre[1]); p = pre[2]; } else if (post) { qty = Number(post[2]); p = post[1]; } else if (dot) { qty = cleanQty(dot[2]); p = dot[1]; }
    p = p.trim();
    if (p) out.push({ text: p, ...(qty != null && (typeof qty !== 'number' || qty > 0) ? { qty } : {}), ...(done ? { done: true } : {}) });
  }
  return out;
}

/** Items → editable text, one per line ("eggs x12", "oil · 1 L"); parseListText reads it back. */
export function listItemsText(items) {
  return (Array.isArray(items) ? items : []).map((x) => (x.qty == null ? x.text : typeof x.qty === 'number' ? `${x.text} x${x.qty}` : `${x.text} · ${x.qty}`)).join('\n');
}

/** A clean list template (id / createdAt / updatedAt / ownerUid kept). Template items carry no ids or ticks. */
export function makeListTemplate(o = {}) {
  const items = (Array.isArray(o.items) ? o.items : [])
    .map((x) => (typeof x === 'string' ? { text: x } : x))
    .map((x) => ({ text: str(x?.text).replace(/\s+/g, ' ').slice(0, LIST_ITEM_MAX), qty: cleanQty(x?.qty) }))
    .filter((x) => x.text)
    .slice(0, LIST_MAX_ITEMS)
    .map((x) => (x.qty != null ? x : { text: x.text }));
  return {
    ...(o.id ? { id: o.id } : {}),
    name: str(o.name).slice(0, 80),
    icon: str(o.icon) || null,
    items,
    departmentId: o.departmentId || null,
    uses: Math.max(0, Math.round(Number(o.uses) || 0)),
    lastUsedAt: Number.isFinite(o.lastUsedAt) ? o.lastUsedAt : null,
    ...(o.createdAt ? { createdAt: o.createdAt } : {}),
    ...(o.updatedAt ? { updatedAt: o.updatedAt } : {}),
  };
}

/**
 * The "Add from template" order: search first (name or any item contains q), then the task's department's templates,
 * then most used, then most recently used, then name.
 */
export function rankListTemplates(templates = [], { departmentId = null, q = '' } = {}) {
  const needle = str(q).toLowerCase();
  const hit = (t) => !needle || str(t.name).toLowerCase().includes(needle) || (t.items || []).some((x) => str(x?.text).toLowerCase().includes(needle));
  return templates.filter((t) => t && !t.deletedAt && hit(t)).slice().sort((a, b) => {
    const da = departmentId && a.departmentId === departmentId ? 0 : 1;
    const dbb = departmentId && b.departmentId === departmentId ? 0 : 1;
    return (da - dbb) || ((b.uses || 0) - (a.uses || 0)) || ((b.lastUsedAt || 0) - (a.lastUsedAt || 0)) || str(a.name).localeCompare(str(b.name));
  });
}

/** Items from a template (fresh ids, unticked), skipping texts already in `have` (case-insensitive). */
export function itemsFromTemplate(tpl, newId, have = []) {
  const seen = new Set(have.map((x) => str(x.text).toLowerCase()));
  const out = [];
  for (const x of makeListTemplate(tpl).items) {
    const k = x.text.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(cleanItem({ text: x.text, qty: x.qty }, newId));
  }
  return out;
}

/**
 * The list a task template carries, onto a new task: from its list template when that's here (its items as they are
 * now), else the items saved on the template. `listTemplates` = the library. Returns makeTaskDoc-ready list input or null.
 */
export function listForTemplate(tplList, listTemplates = []) {
  if (!tplList) return null;
  const lt = tplList.templateId ? listTemplates.find((x) => x.id === tplList.templateId) : null;
  const items = lt ? makeListTemplate(lt).items : (Array.isArray(tplList.items) ? tplList.items : []);
  if (!items.length && !lt) return null;
  return { ...(tplList.templateId ? { templateId: tplList.templateId } : {}), ...(lt?.name || tplList.name ? { name: lt?.name || tplList.name } : {}), items: items.map((x) => ({ text: x.text, ...(x.qty != null ? { qty: x.qty } : {}) })) };
}

/** A task template's list, clean: { templateId?, name?, items: [{ text, qty? }] } or null. */
export function cleanTemplateList(l) {
  if (!l || typeof l !== 'object') return null;
  const t = makeListTemplate({ name: l.name, items: l.items });
  const templateId = str(l.templateId);
  if (!templateId && !t.items.length) return null;
  return { ...(templateId ? { templateId } : {}), ...(t.name ? { name: t.name } : {}), items: t.items };
}
